// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo, useState } from 'react'
import {
  Checkbox,
  ColumnDef,
  Flex,
  Table,
  SortingState,
  useShowHide,
  Typography,
} from '@teamnine/pcj-ui-v2'
import { Description } from '@/utils/material-icons'
import { useTheme } from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { Template as ITemplate } from '@/services/templates'
import { toMilliseconds } from '@/utils/helpers'
import { useGetTemplates } from '@/app/Templates/hooks/useGetTemplates'
import Page from '@/app/shared/components/Layout/Page'
import PageHeader from '@/app/shared/components/PageHeader'
import UploadTemplateModal from '@/app/Templates/components/UploadTemplateModal'
import Panel from '@/app/shared/components/Panel'
import LinkButton from '@/app/shared/components/LinkButton'
import TemplateStatusTag from '@/app/Templates/components/TemplateStatusTag'

const PAGE_SIZE = 30

type Row = {
  row: { original: ITemplate }
}

export default function Templates() {
  const navigate = useNavigate()
  const { colors } = useTheme()
  const uploadTemplateModal = useShowHide()
  const [showGolden, setShowGolden] = useState(false)
  const [showDrafts, setShowDrafts] = useState(false)
  const [showPublished, setShowPublished] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [pageIndex, setPageIndex] = useState(0)

  const { data: currentTemplates = [], isLoading } = useGetTemplates()

  const filteredTemplates = useMemo(() => {
    return currentTemplates?.filter((template) => {
      const matchesGolden = showGolden && template?.golden
      const matchesDraft = showDrafts && !template?.published
      const matchesPublished = showPublished && template?.published
      const anyFilters = showGolden || showDrafts || showPublished

      const matchesCheckbox =
        !anyFilters || matchesGolden || matchesDraft || matchesPublished

      const matchesSearch = template?.name
        .toLowerCase()
        .includes(searchString.toLowerCase())

      return matchesCheckbox && matchesSearch
    })
  }, [currentTemplates, showGolden, showDrafts, showPublished, searchString])

  const sortedTemplates = useMemo(() => {
    if (!sorting.length) return filteredTemplates

    const { id, desc } = sorting[0]

    return [...filteredTemplates].sort((a, b) => {
      let comparison = 0

      if (id === 'name') {
        comparison = a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      } else if (id === 'status') {
        const aStatus = a.published ? 3 : a.golden ? 2 : 1
        const bStatus = b.published ? 3 : b.golden ? 2 : 1
        comparison = aStatus - bStatus
        if (comparison === 0) {
          comparison = a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
      } else if (id === 'createdAt') {
        const aMilliseconds = toMilliseconds(a.createdAt)
        const bMilliseconds = toMilliseconds(b.createdAt)
        comparison = aMilliseconds - bMilliseconds
      }

      return desc ? -comparison : comparison
    })
  }, [filteredTemplates, sorting])

  const pagedTemplates = useMemo(() => {
    const start = pageIndex * PAGE_SIZE
    return sortedTemplates.slice(start, start + PAGE_SIZE)
  }, [sortedTemplates, pageIndex])

  const columns: ColumnDef<ITemplate>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name' as const,
        enableSorting: true,
        cell: ({ row }: Row) => (
          <LinkButton
            label={row.original.name}
            onClick={() => navigate(`/templates/${row?.original.id}`)}
          />
        ),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        accessorKey: 'createdAt' as const,
        enableSorting: true,
        cell: ({ row }: Row) => {
          const date = row.original.createdAt
          const milliseconds = toMilliseconds(date)
          return milliseconds ? new Date(milliseconds).toLocaleString() : ''
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: true,
        cell: ({ row }: Row) => {
          const template = row.original
          return <TemplateStatusTag template={template} />
        },
      },
    ],
    [navigate]
  )

  return (
    <Page>
      <Page.Content>
        <Panel p={16} borderRadius={12} useContent={false}>
          <Flex direction="column" gap={16}>
            <PageHeader
              title="Templates"
              action={<UploadTemplateModal modal={uploadTemplateModal} />}
            />
            <Table<ITemplate>
              borderless
              data={pagedTemplates}
              isLoading={isLoading}
              columns={columns}
              pagination={{
                mode: 'paged',
                pageIndex,
                pageSize: PAGE_SIZE,
                totalCount: sortedTemplates.length,
                onPageChange: ({ pageIndex }) => {
                  setPageIndex(pageIndex)
                },
              }}
              sorting={{
                state: sorting,
                onSortingChange: (next) => {
                  setSorting(next)
                  setPageIndex(0)
                },
              }}
              search={{
                type: 'single',
                onSearch: (val: string[]) => {
                  setSearchString(val[0])
                  setPageIndex(0)
                },
                placeholder: 'Enter template name',
              }}
              empty={{
                title: 'There are no templates.',
                Icon: Description,
              }}
              actions={
                <Flex gap={16} p={16} align="center" flex="1">
                  <Typography variant="body-m-regular" color={colors.nggray950}>
                    Template Type:
                  </Typography>
                  <Checkbox
                    checked={showGolden}
                    onChange={() => {
                      setShowGolden((prev) => !prev)
                      setPageIndex(0)
                    }}
                  >
                    <Typography
                      variant="body-m-regular"
                      color={colors.nggray950}
                    >
                      Golden
                    </Typography>
                  </Checkbox>
                  <Checkbox
                    checked={showDrafts}
                    onChange={() => {
                      setShowDrafts((prev) => !prev)
                      setPageIndex(0)
                    }}
                  >
                    <Typography
                      variant="body-m-regular"
                      color={colors.nggray950}
                    >
                      Draft
                    </Typography>
                  </Checkbox>
                  <Checkbox
                    checked={showPublished}
                    onChange={() => {
                      setShowPublished((prev) => !prev)
                      setPageIndex(0)
                    }}
                  >
                    <Typography
                      variant="body-m-regular"
                      color={colors.nggray950}
                    >
                      Published
                    </Typography>
                  </Checkbox>
                </Flex>
              }
            />
          </Flex>
        </Panel>
      </Page.Content>
    </Page>
  )
}
